import React, { memo, useState } from "react";
import CRMTable from "@metacrm/metacrm-material-ui/dist/Table";
import { Box, Menu, MenuItem } from "@mui/material";
import { map, isEmpty } from "lodash-es";
import { useTheme } from "@mui/styles";
import { useIntl } from "react-intl";
import EmptyIconStatus from "components/EmptyState/EmptyIconStatus";
import TextDropdown from "../TextDropdown";

const Top10Table = ({
  chartData,
  type,
  isLoading,
  fetchMentionsDetail,
  handleExclude,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleRowClick = (targetId, row, event) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const headCells = [
    {
      show: true,
      disabledSort: true,
      id: "keyword",
      label: (
        <Box>
          {formatMessage({ id: "keywordTracking.leaderboard.header.keyword" })}
        </Box>
      ),
      width: 150,
    },
    {
      show: true,
      disabledSort: true,
      id: "count",
      label: (
        <Box>
          {formatMessage({ id: "keywordTracking.leaderboard.header.mention" })}
        </Box>
      ),
      width: 150,
    },
    {
      show: true,
      disabledSort: true,
      id: "variation",
      label: <Box>{formatMessage({ id: "global.metrics.variation" })}</Box>,
      width: 150,
    },
  ];

  const rowData = map(chartData, (item, index) => {
    return {
      _id: item.keywordIds,
      keyword: item.keyword,
      count: item.count,
      variation: item.variation,
    };
  }).slice(0, 10);

  const renderContents = (item, i) => {
    const contents = [
      <Box slot="keyword">
        <Box display={"flex"} alignItems={"center"}>
          <Box mr="5px"> {i + 1}.</Box>
          {item.keyword}
        </Box>
      </Box>,
      <Box slot="count">{item.count}</Box>,
      <Box
        slot="variation"
        sx={{
          color:
            item.variation > 0
              ? theme.customColors.green[500]
              : theme.customColors.pink[500],
        }}>
        {item.variation}
      </Box>,
    ];

    return contents;
  };

  return (
    <Box
      sx={{
        height: "100%",
        "& .MuiTable-root": {
          "& td": {
            fontWeight: 500,
          },
        },

        ...(isEmpty(chartData) && {
          "& td": {
            padding: "0 !important",
            height: "100%",
            marginBottom: "0",
            "& .nodata-title": {
              color: `${theme.customColors.grey[700]} !important`,
              fontSize: "14px",
            },
          },
        }),
      }}>
      <CRMTable
        rowData={rowData}
        headers={headCells}
        contents={map(rowData, (item, i) => renderContents(item, i))}
        noDataComponent={
          <Box sx={{ background: theme.customColors.white }} height="300px">
            <EmptyIconStatus
              icon="table"
              emptyTitle={formatMessage({ id: "emptyState.table.title" })}
              emptyDescription={formatMessage({
                id: "emptyState.table.description",
              })}
            />
          </Box>
        }
        onRowClick={(id, row, event) => handleRowClick(id, row, event)}
        containerStyle={{
          height: "100%",
          "& .MuiTable-root": {
            minWidth: "300px",
          },
          "& thead": {
            "& th": {
              padding: "20px 20px 18px",
              borderRight: `0 !important`,
            },
          },
          "& tbody": {
            "& td": {
              padding: "17px 20px 17px",
              borderRight: 0,
            },
          },
        }}
        isLoading={isLoading}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 36,
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        slotProps={{
          paper: {
            sx: {
              boxShadow: "0px 9.348px 18.696px 0px rgba(0, 0, 0, 0.07)",
              borderRadius: "4px",
              backgroundColor: "#FFFFFF",
              padding: "4px",
              minWidth: "156px",
            },
          },
        }}
        MenuListProps={{
          sx: {
            padding: 0,
          },
        }}
        transitionDuration={0}>
        <MenuItem
          onClick={() => {
            handleClose();
            fetchMentionsDetail({
              type,
              keywordIds: selectedRow?._id,
            });
          }}
          sx={{
            height: "32px",
            padding: "0 8px",
            display: "flex",
            alignItems: "center",
            columnGap: "4px",
            color: theme.customColors.grey[700],
            fontSize: "12px",
            cursor: "pointer",
            whiteSpace: "nowrap",
            "&:hover": {
              backgroundColor: "#FCFAFF",
            },
          }}>
          <i className="meta-crm-icon-ic_message font-size-16" />
          <div
            style={{
              whiteSpace: "nowrap",
              width: "fit-content",
              fontSize: "12px",
            }}>
            {`View mentions with "${selectedRow?.keyword}"`}
          </div>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            handleExclude({
              excludeType: "keyword",
              platformType: type,
              identifier: {
                keyword: selectedRow?.keyword,
                _id: selectedRow?._id,
              },
            });
          }}
          sx={{
            height: "32px",
            padding: "0 8px",
            display: "flex",
            alignItems: "center",
            columnGap: "4px",
            color: theme.customColors.grey[700],
            fontSize: "12px",
            cursor: "pointer",
            whiteSpace: "nowrap",
            "&:hover": {
              backgroundColor: "#FCFAFF",
            },
          }}>
          <i className="meta-crm-icon-ic_visibilityOff font-size-16" />
          <div
            style={{
              fontSize: "12px",
            }}>{`Exclude "${selectedRow?.keyword}"`}</div>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default memo(Top10Table);
